import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name_ar: '', name_en: '', location: '', description: '', badge_color: '#0b8793', start_date: '', end_date: '', registration_deadline: '', status: 'open' };
const statusLabels = { draft: 'مسودة', open: 'مفتوحة للتسجيل', closed: 'مغلقة', archived: 'مؤرشفة' };

export default function EventsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === 'admin';
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const { data } = await client.get('/events');
    setEvents(data);
    const entries = await Promise.all(data.map(async ev => {
      try {
        const r = await client.get('/participants', { params: { event_id: ev.id, limit: 1 } });
        return [ev.id, r.data.total];
      } catch { return [ev.id, 0]; }
    }));
    setCounts(Object.fromEntries(entries));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (ev) => {
    setForm({
      name_ar: ev.name_ar, name_en: ev.name_en || '', location: ev.location || '',
      description: ev.description || '', badge_color: ev.badge_color || '#0b8793',
      start_date: ev.start_date || '', end_date: ev.end_date || '',
      registration_deadline: ev.registration_deadline || '', status: ev.status
    });
    setEditingId(ev.id);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (editingId) await client.put(`/events/${editingId}`, form);
    else await client.post('/events', form);
    setShowForm(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('تأكد من حذف الفعالية؟ سيتم حذف كل المشاركين والنتائج المرتبطة بها.')) return;
    await client.delete(`/events/${id}`);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <h2 style={{ margin: 0 }}>الفعاليات</h2>
        {isAdmin && <button className="btn btn-primary" onClick={openCreate}>+ فعالية جديدة</button>}
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'تعديل الفعالية' : 'إضافة فعالية جديدة'}</h3>
          <form onSubmit={submit}>
            <div className="form-grid">
              <div className="form-field"><label>اسم الفعالية (عربي) *</label>
                <input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} required /></div>
              <div className="form-field"><label>اسم الفعالية (English)</label>
                <input value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} /></div>
              <div className="form-field"><label>الموقع</label>
                <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
              <div className="form-field"><label>الحالة</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></div>
              <div className="form-field"><label>تاريخ البداية</label>
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="form-field"><label>تاريخ النهاية</label>
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="form-field"><label>آخر موعد للتسجيل</label>
                <input type="date" value={form.registration_deadline} onChange={e => setForm({ ...form, registration_deadline: e.target.value })} /></div>
              <div className="form-field"><label>لون البطاقة الرئيسي</label>
                <input type="color" value={form.badge_color} onChange={e => setForm({ ...form, badge_color: e.target.value })} /></div>
            </div>
            <div className="form-field" style={{ marginTop: 14 }}>
              <label>وصف الفعالية (يظهر في صفحة التسجيل العامة)</label>
              <textarea rows="3" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary">{editingId ? 'حفظ التعديلات' : 'إنشاء الفعالية'}</button>
              <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>إلغاء</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-2">
        {events.map(ev => (
          <div className="card" key={ev.id} style={{ borderTop: `4px solid ${ev.badge_color || '#0b8793'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <h3 style={{ margin: '0 0 4px' }}>{ev.name_ar}</h3>
                <div style={{ color: 'var(--muted)', fontSize: 13 }}>{ev.location}</div>
              </div>
              <span className={`badge status-${ev.status === 'open' ? 'approved' : ev.status === 'closed' ? 'rejected' : 'draft'}`}>
                {statusLabels[ev.status]}
              </span>
            </div>
            <div style={{ margin: '14px 0', fontSize: 13, color: 'var(--muted)' }}>
              {ev.start_date} → {ev.end_date}
            </div>
            <div style={{ fontSize: 13, marginBottom: 14 }}>
              عدد المشاركين المسجلين: <b className="mono">{counts[ev.id] ?? '—'}</b>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" onClick={() => navigate(`/events/${ev.id}`)}>فتح الفعالية</button>
              {isAdmin && <button className="btn btn-outline" onClick={() => openEdit(ev)}>تعديل</button>}
              {isAdmin && <button className="btn btn-danger" onClick={() => remove(ev.id)}>حذف</button>}
            </div>
          </div>
        ))}
        {events.length === 0 && <div className="card">لا توجد فعاليات بعد. {isAdmin && <button className="btn btn-primary" onClick={openCreate}>أنشئ أول فعالية</button>}</div>}
      </div>
    </div>
  );
}
